import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapContainer, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { Box, CircularProgress, Typography, Stack } from '@mui/material';
import CellTowerIcon from '@mui/icons-material/CellTower';
import 'leaflet/dist/leaflet.css';

import { loadAllSites, loadNetworkCombos, applyFilters } from '../../actions/siteActions';
import { GetSiteActivitiesRequest } from '../../requers/siteRequest';
import { computeActivityStatus } from '../../utils/siteStatus';
import { useVisibleSites } from '../../hooks/useVisibleSites';
import { useAutozoom } from '../../hooks/useAutozoom';

import FiltersPanel from '../filters/FiltersPanel';
import ActivityTitleFilter from '../filters/ActivityTitleFilter';
import SiteGrid     from '../grid/SiteGrid';
import CanvasLayer  from './CanvasLayer';
import TacLayer     from './TacLayer';
import MapLegend    from './MapLegend';
import { buildPopupHtml } from './buildPopupHtml';

// ── Title overlay ────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TitleOverlay = ({ filters }) => {
  const parts = [];
  if (filters.technologies?.length > 0)
    parts.push({ label: filters.technologies.join(', '), color: '#4FC3F7' });
  if (filters.vendors?.length > 0)
    parts.push({ label: filters.vendors.join(', '), color: '#B39DDB' });
  if (filters.servers?.length > 0)
    parts.push({ label: filters.servers.join(', '), color: '#80CBC4' });
  if (filters.tacs?.length > 0)
    parts.push({ label: 'TAC: ' + filters.tacs.join(', '), color: '#FFCC02' });
  const from = fmtDate(filters.startDate);
  const to   = fmtDate(filters.endDate);
  if (from && to)
    parts.push({ label: from + ' → ' + to, color: '#78909C' });

  return (
    <Box sx={{
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
      zIndex: 1200, pointerEvents: 'none',
      backgroundColor: 'rgba(13,27,42,0.88)',
      border: '1px solid #1E3A5F', borderRadius: 2,
      px: 2, py: 0.6,
      display: 'flex', alignItems: 'center', gap: 0,
      whiteSpace: 'nowrap',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <CellTowerIcon sx={{ color: '#4FC3F7', fontSize: 14 }} />
        <Typography sx={{ color: '#E0E7FF', fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Activities Map
        </Typography>
      </Stack>
      {parts.map(function(p, i) {
        return (
          <Stack key={i} direction="row" alignItems="center">
            <Typography sx={{ color: '#1E3A5F', fontSize: 11, mx: 0.8 }}>·</Typography>
            <Typography sx={{ color: p.color, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              {p.label}
            </Typography>
          </Stack>
        );
      })}
    </Box>
  );
};

const MapView = () => {
  const dispatch = useDispatch();
  const { sites, loading, error, filters } = useSelector((s) => s.sites);
  const mapRef                             = useRef(null);
  const [selectedSite, setSelectedSite]   = useState(null);
  const [colourMode, setColourMode]        = useState('status');
  const [hideInactive, setHideInactive]    = useState(false);
  const [hideOld, setHideOld]              = useState(false);
  const [tileLayer, setTileLayer]          = useState('light');
  const [showTacPolygons, setShowTacPolygons] = useState(false);

  const handleActivityStatusChange = useCallback(function(newStatus) {
    dispatch(applyFilters({ ...filters, activityStatus: newStatus }));
    if (newStatus === 'Open') { setHideInactive(false); setHideOld(false); }
  }, [dispatch, filters]);

  const { colouredSites, visibleSites, featureStats, stats } = useVisibleSites(sites, filters, hideInactive);

  useAutozoom(mapRef, visibleSites, loading, hideInactive);

  useEffect(function() {
    dispatch(loadNetworkCombos());
    dispatch(loadAllSites());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActivities = useCallback(async function(site) {
    setSelectedSite({ ...site, activities: [], loading: true });
    try {
      var res = await GetSiteActivitiesRequest({
        csr:            site.site,
        technology:     site.tech,
        vendor:         site.vendor,
        omcId:          site.server,
        tac:            site.tac != null ? site.tac : site.tAC,
        activityStatus: filters.activityStatus || 'Open',
      });
      var actStatus = filters.activityStatus || 'Open';
      var activities = (res.data || []).map(function(act) {
        return {
          ...act,
          title:     act.Title     != null ? act.Title     : act.title,
          feature:   act.Feature   != null ? act.Feature   : act.feature,
          status:    act.Status    != null ? act.Status    : act.status,
          startDate: act.StartDate != null ? act.StartDate : act.startDate,
          endDate:   act.EndDate   != null ? act.EndDate   : act.endDate,
          team:      act.Team      != null ? act.Team      : act.team,
          owner:     act.Owner     != null ? act.Owner     : act.owner,
          _status:   computeActivityStatus(act, actStatus),
        };
      });
      setSelectedSite(function(prev) { return prev ? { ...prev, activities: activities, loading: false } : null; });
    } catch (e) {
      void e;
      setSelectedSite(function(prev) { return prev ? { ...prev, activities: [], loading: false } : null; });
    }
  }, [filters.activityStatus]);

  const handleCanvasClick = useCallback(function(site) {
    var map = mapRef.current;
    if (map) {
      var lat = parseFloat(site.latitude);
      var lng = parseFloat(site.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        L.popup({ maxWidth: 240, className: 'site-popup' })
          .setLatLng([lat, lng])
          .setContent(buildPopupHtml(site, filters.activityStatus || 'Open'))
          .openOn(map);
      }
    }
    var count = site.activitiesCount != null ? site.activitiesCount : (site.ActivitiesCount != null ? site.ActivitiesCount : 0);
    if (!count) { setSelectedSite(null); return; }
    fetchActivities(site);
  }, [fetchActivities]);

  // Popup closes (click outside) → close SiteGrid
  // mapRef.current is populated after MapContainer mounts — use a small delay to register
  useEffect(function() {
    var timer = setTimeout(function() {
      var map = mapRef.current;
      if (!map) return;
      var handler = function() { setSelectedSite(null); };
      map.on('popupclose', handler);
      // cleanup stored on map instance for removal
      map._gridCloseHandler = handler;
    }, 300);
    return function() {
      clearTimeout(timer);
      var map = mapRef.current;
      if (map && map._gridCloseHandler) {
        map.off('popupclose', map._gridCloseHandler);
      }
    };
  }, []);

  // SiteGrid X button → also close popup
  const handleGridClose = useCallback(function() {
    setSelectedSite(null);
    var map = mapRef.current;
    if (map) map.closePopup();
  }, []);

  return (
    <Box sx={{
      position: 'relative', width: '100%', flex: 1,
      display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
    }}>
      <Box sx={{ position: 'relative', flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>

        {loading && (
          <Box sx={{
            position: 'absolute', top: 52, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1200, backgroundColor: 'rgba(13,27,42,0.95)', borderRadius: 3,
            px: 2.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1.5,
            border: '1px solid #1E3A5F', whiteSpace: 'nowrap',
          }}>
            <CircularProgress size={14} sx={{ color: '#4FC3F7' }} />
            <Typography variant="caption" sx={{ color: '#E0E7FF', fontWeight: 600 }}>Loading network…</Typography>
          </Box>
        )}

        {error && !loading && (
          <Box sx={{
            position: 'absolute', top: 52, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1200, backgroundColor: 'rgba(180,30,30,0.92)', borderRadius: 3,
            px: 2.5, py: 0.8, border: '1px solid #f44336', whiteSpace: 'nowrap',
          }}>
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>⚠ {error}</Typography>
          </Box>
        )}

        <FiltersPanel />
        <ActivityTitleFilter sites={colouredSites} />
        <TitleOverlay filters={filters} />
        <MapLegend
          stats={stats}
          total={visibleSites.length}
          colourMode={colourMode}
          onColourModeChange={setColourMode}
          featureStats={
            (filters.features && filters.features.length > 0)
              ? Object.fromEntries(Object.entries(featureStats).filter(([k]) => (filters.features).includes(k)))
              : featureStats
          }
          hideInactive={hideInactive}
          onHideInactiveChange={setHideInactive}
          activityStatus={filters.activityStatus || 'Open'}
          onActivityStatusChange={handleActivityStatusChange}
          tileLayer={tileLayer}
          onTileLayerChange={setTileLayer}
          selectedFeatures={filters.features || []}
          showTacPolygons={showTacPolygons}
          onTacPolygonsChange={setShowTacPolygons}
        />

        <MapContainer
          center={[54.5, -2.5]}
          zoom={6}
          minZoom={5}
          style={{ width: '100%', height: '100%' }}
          ref={mapRef}
          whenReady={function(e) { e.target.setView([54.5, -2.5], 6); }}
        >
          <TileLayer
            key={tileLayer}
            attribution={
              tileLayer === 'satellite'
                ? '&copy; <a href="https://www.esri.com">Esri</a>'
                : '&copy; <a href="https://www.openstreetmap.org">OpenStreetMap</a> &copy; <a href="https://carto.com">CARTO</a>'
            }
            url={
              tileLayer === 'dark'
                ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                : tileLayer === 'light'
                ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            }
          />
          <TacLayer sites={colouredSites} visible={showTacPolygons} selectedTitles={filters.selectedTitles || []} />
          <CanvasLayer
            key={'canvas-' + (filters.features || []).join(',')}
            sites={visibleSites}
            onSiteClick={handleCanvasClick}
            colourMode={colourMode}
            activityStatus={filters.activityStatus || 'Open'}
            tileLayer={tileLayer}
            selectedFeatures={filters.features || []}
            selectedTitles={filters.selectedTitles || []}
            hideInactive={hideInactive}
          />
        </MapContainer>
      </Box>

      <SiteGrid
        selectedSite={selectedSite}
        onClose={handleGridClose}
        hideOld={hideOld}
        onHideOldChange={setHideOld}
      />
    </Box>
  );
};

export default MapView;